import type { UUID } from '@cobliteam/common-types';

import { Environment } from '@cobliteam/react-environments';

const { oneSignalAppId } = Environment.getInstance().getEnvironment();

declare const OneSignalDeferred: any;
declare const OneSignal: any; // OneSignal global variable initialized on index.html

let hasBeenInitialized = false;
// Tracks whether OneSignal.init() was called — survives error resets to prevent "SDK already initialized"
let oneSignalInitCalled = false;

export class Notifications {
  private static initializationPromise: Promise<void> | null = null;

  public static isInitialized = () => hasBeenInitialized;

  public static initialize = (fleetId: UUID, fleetName: string, userId: UUID): Promise<void> => {
    // If we already have an initialization promise, wait for it and then set the user
    if (Notifications.initializationPromise) {
      return Notifications.initializationPromise.then(() => {
        return Notifications.setUser(fleetId, fleetName, userId);
      });
    }

    // Create a new initialization promise
    Notifications.initializationPromise = new Promise((resolve, reject) => {
      if (hasBeenInitialized) {
        console.warn('Notifications has already been initialized');
        Notifications.setUser(fleetId, fleetName, userId).then(resolve).catch(reject);
        return;
      }

      // If init() was already called in a prior attempt (even one that failed post-init),
      // skip re-initialization and just set the user.
      if (oneSignalInitCalled) {
        hasBeenInitialized = true;
        Notifications.setUser(fleetId, fleetName, userId).then(resolve).catch(reject);
        return;
      }

      OneSignalDeferred.push(async (OneSignal: any) => {
        try {
          // Guard against re-calling init() on retries: if a prior attempt already called
          // OneSignal.init() (even if post-init steps failed), calling it again throws
          // "SDK already initialized". Use oneSignalInitCalled which survives error resets.
          if (oneSignalInitCalled) {
            hasBeenInitialized = true;
            await Notifications.setUser(fleetId, fleetName, userId);
            resolve();
            return;
          }

          await OneSignal.init({
            appId: oneSignalAppId,
            autoRegister: true,
            // ⚠️ WARNING: Service Worker Configuration for OneSignal
            // Ensure the correct path and scope are set for the service worker.
            // In this instance, the service worker is located in the public/push/original directory of the project.
            // Modifying this path could conflict with other service workers (like PWA's service worker) and potentially break the entire application.
            // Proceed with caution make sure you understand the implications of changing the service worker path and scope.
            serviceWorkerParam: { scope: '/push/onesignal/' },
            serviceWorkerPath: '/push/onesignal/OneSignalSDKWorker.js',
          });

          oneSignalInitCalled = true;
          hasBeenInitialized = true;

          // show the native prompt
          await OneSignal.Slidedown.promptPush();

          await Notifications.setUser(fleetId, fleetName, userId);

          // when the user changes the permission, we need to update the settings
          await OneSignal.Notifications.addEventListener('permissionChange');

          resolve();
        } catch (error) {
          // "SDK already initialized" is not a real failure — treat it as success
          const message = error instanceof Error ? error.message : String(error);
          if (message.includes('already initialized')) {
            console.warn('OneSignal SDK was already initialized, continuing.');
            oneSignalInitCalled = true;
            hasBeenInitialized = true;
            try {
              await Notifications.setUser(fleetId, fleetName, userId);
            } catch (setUserError) {
              console.error('Failed to set OneSignal user after re-init:', setUserError);
            }
            resolve();
            return;
          }

          console.error('Failed to initialize OneSignal:', error);
          hasBeenInitialized = false;
          Notifications.initializationPromise = null;
          reject(error);
        }
      });
    });

    return Notifications.initializationPromise;
  };

  public static setUser = async (fleetId: UUID, fleetName: string, userId: UUID): Promise<void> => {
    return new Promise((resolve, reject) => {
      OneSignalDeferred.push(async (OneSignal: any) => {
        try {
          // create a new user
          await OneSignal.login(userId);

          // add tags
          await OneSignal.User.addTags({
            fleet_id: fleetId,
            fleet_name: fleetName,
          });

          console.info('OneSignal user set!');
          resolve();
        } catch (error) {
          console.error('Failed to set OneSignal user:', error);
          reject(error);
        }
      });
    });
  };

  public static hasNotificationPermission = (): boolean => {
    // typeof guard: Safari throws ReferenceError when optional-chaining an undeclared global
    if (typeof OneSignal === 'undefined') return false;
    return OneSignal?.Notifications?.permission ?? false;
  };

  public static slidedownPromptPush(options?: any): Promise<void> {
    return new Promise((resolve, reject) => {
      try {
        OneSignalDeferred?.push((OneSignal: any) => {
          OneSignal.Slidedown.promptPush(options)
            .then((value: any) => resolve(value))
            .catch((error: any) => reject(error));
        });
      } catch (error) {
        reject(error);
      }
    });
  }

  public static pushSubscriptionOptIn(): Promise<void> {
    return new Promise((resolve, reject) => {
      try {
        OneSignalDeferred?.push((OneSignal: any) => {
          OneSignal.User.PushSubscription.optIn()
            .then((value: any) => resolve(value))
            .catch((error: any) => reject(error));
        });
      } catch (error) {
        reject(error);
      }
    });
  }

  public static pushSubscriptionOptOut(): Promise<void> {
    return new Promise((resolve, reject) => {
      try {
        OneSignalDeferred?.push((OneSignal: any) => {
          OneSignal.User.PushSubscription.optOut()
            .then((value: any) => resolve(value))
            .catch((error: any) => reject(error));
        });
      } catch (error) {
        reject(error);
      }
    });
  }

  public static getUserInfo() {
    if (typeof OneSignal === 'undefined') return;
    Promise.all([
      OneSignal.Notifications.permission,
      OneSignal.User.PushSubscription.id,
      OneSignal.User.PushSubscription.token,
      OneSignal.User.PushSubscription.optedIn,
      OneSignal.context.serviceWorkerManager.getActiveState(),
    ])
      .then(([isSubscribed, subscriptionId, pushToken, optedIn, serviceWorkerActive]) => {
        console.info('What is the current URL of this page?', location.href);
        console.info("Is a service worker registered and active? (should be false on Safari, otherwise should be 'OneSignal Worker')?", serviceWorkerActive);
        console.info('Are you subscribed, in the browser?', isSubscribed);
        console.info('Are you opted-in, in OneSignal?', optedIn);
        console.info('What is your OneSignal Subscription ID?', subscriptionId);
        console.info('What is your Push Token?', pushToken);
      })
      .catch((e) => {
        console.error('Issue determining whether push is enabled:', e);
      });
  }

  public static updateSubscription = async (value: Boolean) => {
    if (!hasBeenInitialized) await Notifications.slidedownPromptPush({ force: true });
    if (value) {
      const hasPemission = Notifications.hasNotificationPermission();
      if (!hasPemission) await Notifications.pushSubscriptionOptIn();
    } else {
      await Notifications.pushSubscriptionOptOut();
    }
  };
}
